import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getInternalUserById } from './userService';
import { getPillarEmoji } from '../utils/portalEmojis';

// Global routing template - used by all portal pages regardless of user
// Schedule links match "Schedule a Strategy Call" embeds on PortalSales, PortalOperations, Technology, Finance
const globalRoutingTemplate = {
  sales: {
    scheduleLink: 'https://calendly.com/ken-zwz1/sales-strategy-call',
    questionAssignee: 'ken@reignmediagroup.com'
  },
  operations: {
    scheduleLink: 'https://calendly.com/daryl-yxbw/operations-strategy-call?hide_gdpr_banner=1',
    questionAssignee: 'daryl@reignmediagroup.com'
  },
  finance: {
    scheduleLink: 'https://calendly.com/ianh-fm8t/finance-strategy-call?hide_gdpr_banner=1',
    questionAssignee: 'finance@reignmediagroup.com'
  },
  technology: {
    scheduleLink: 'https://calendly.com/ianr-lhxe/technology-strategy-call?hide_gdpr_banner=1',
    questionAssignee: 'ian@reignmediagroup.com'
  },
  marketing: {
    scheduleLink: 'https://calendly.com/parker-reignmediagroup',
    questionAssignee: 'parker@reignmediagroup.com'
  },
  hr: {
    scheduleLink: 'https://calendar.app.google/E7SpMRxk9DU19hP36',
    questionAssignee: 'hr@reignmediagroup.com'
  }
};

// Get pillars settings for a user - now always returns global routing template
export const getPillarsSettings = async (userId) => {
  try {
    // Try to get from global routing template document first
    const globalDoc = await getDoc(doc(db, 'globalSettings', 'routingTemplate'));
    if (globalDoc.exists()) {
      console.log('Using global routing template from Firestore');
      return globalDoc.data();
    }
    
    // If no global document exists, return the hardcoded template
    console.log('Using hardcoded global routing template');
    return globalRoutingTemplate;
  } catch (error) {
    console.error('Error fetching pillars settings:', error);
    return globalRoutingTemplate;
  }
};

// Update pillars settings - now saves to global routing template
export const updatePillarsSettings = async (userId, pillarsSettings) => {
  try {
    console.log('Saving global routing template settings');
    console.log('Pillars settings to save:', pillarsSettings);
    
    // Save to global routing template document
    const globalDocRef = doc(db, 'globalSettings', 'routingTemplate');
    await setDoc(globalDocRef, {
      ...pillarsSettings,
      lastUpdated: new Date().toISOString(),
      updatedBy: userId
    });
    console.log('Global routing template saved successfully');
    
    return true;
  } catch (error) {
    console.error('Error updating global routing template:', error);
    throw error;
  }
};

// Get schedule link for a specific pillar
export const getPillarScheduleLink = async (userId, pillar) => {
  try {
    const settings = await getPillarsSettings(userId);
    console.log(`Getting schedule link for ${pillar}:`, settings[pillar]?.scheduleLink);
    const result = settings[pillar]?.scheduleLink;
    console.log(`Final schedule link for ${pillar}:`, result);
    return result;
  } catch (error) {
    console.error('Error getting pillar schedule link:', error);
    return globalRoutingTemplate[pillar]?.scheduleLink;
  }
};

// Get question assignee for a specific pillar
export const getPillarQuestionAssignee = async (userId, pillar) => {
  try {
    const settings = await getPillarsSettings(userId);
    return settings[pillar]?.questionAssignee;
  } catch (error) {
    console.error('Error getting pillar question assignee:', error);
    return globalRoutingTemplate[pillar]?.questionAssignee;
  }
};

// Get question assignee user details for a specific pillar
export const getPillarQuestionAssigneeUser = async (userId, pillar) => {
  try {
    const assigneeEmail = await getPillarQuestionAssignee(userId, pillar);
    if (assigneeEmail) {
      // Try to find the internal user by email to get their real name
      try {
        const internalUser = await getInternalUserById(assigneeEmail);
        if (internalUser) {
          return {
            id: internalUser.id,
            name: internalUser.name || `${internalUser.firstName || ''} ${internalUser.lastName || ''}`.trim(),
            email: internalUser.email || assigneeEmail
          };
        }
      } catch (error) {
        console.log('Could not find internal user by email, using email as name');
      }
      
      // Fallback: return email as name if no internal user found
      return {
        id: assigneeEmail,
        name: assigneeEmail,
        email: assigneeEmail
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting pillar question assignee user:', error);
    return null;
  }
};

// Pillar display names
export const getPillarDisplayName = (pillar) => {
  const displayNames = {
    sales: 'Sales',
    operations: 'Operations',
    finance: 'Finance',
    technology: 'Technology',
    marketing: 'Marketing',
    hr: 'HR'
  };
  return displayNames[pillar] || pillar;
};

// Pillar icons — sourced from the canonical portal emoji map so the sidebar,
// dashboard, and every department hub stay in sync.
export const getPillarIcon = (pillar) => getPillarEmoji(pillar); 